import { useEffect, useState } from "react";
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { getBackToSalesReasons, updateInstallation } from "../../../services/installationService";

export default function BackToSalesButton({ installation, onUpdated }) {
  const [isOpen, setIsOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const identifier = installation?.uid ?? installation?.deal_id;
  const currentReasonValue = installation?.back_to_sales_reason?.value ?? "";
  const currentComment = installation?.back_to_sales_comment ?? "";
  const isAlreadyBackToSales = Boolean(currentReasonValue);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setIsLoadingReasons(true);
    getBackToSalesReasons()
      .then(({ data }) => {
        if (!cancelled) setReasons(data ?? []);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load Back to Sales reasons");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingReasons(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleOpen = () => {
    setSelectedReason(currentReasonValue);
    setComment(currentComment);
    setIsOpen(true);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setIsOpen(false);
  };

  const handleConfirm = async () => {
    if (!selectedReason || !identifier) return;
    setIsSubmitting(true);
    try {
      const { data } = await updateInstallation(identifier, {
        back_to_sales_reason: selectedReason,
        back_to_sales_comment: comment.trim() || null,
      });
      toast.success(isAlreadyBackToSales ? "Back to Sales updated" : "Installation sent back to sales");
      onUpdated?.(data);
      setIsOpen(false);
    } catch {
      toast.error("Failed to send back to sales");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button color="warning" size="sm" onClick={handleOpen}>
        <i className="fas fa-undo mr-1" />
        {isAlreadyBackToSales ? "Edit Back to Sales" : "Back to Sales"}
      </Button>

      <Modal isOpen={isOpen} toggle={handleClose} size="md" contentClassName="bg-modal">
        <ModalHeader toggle={handleClose}>{isAlreadyBackToSales ? "Edit Back to Sales" : "Back to Sales"}</ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-3">
            This flags the installation as blocked and notifies the Sales team via HubSpot and Zendesk.
          </p>
          {isLoadingReasons ? (
            <div className="text-center py-3">
              <Spinner color="primary" size="sm" />
            </div>
          ) : (
            <FormGroup tag="fieldset" className="mb-3">
              <legend className="col-form-label small text-uppercase font-weight-bold">Reason</legend>
              {reasons.map((reason) => (
                <FormGroup check key={reason.value} className="mb-2">
                  <Input
                    type="radio"
                    id={`bts-reason-${reason.value}`}
                    name="back-to-sales-reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={() => setSelectedReason(reason.value)}
                  />
                  <Label check htmlFor={`bts-reason-${reason.value}`}>
                    <span className="font-weight-bold">{reason.label}</span>
                    <span className="d-block text-muted small">{reason.description}</span>
                  </Label>
                </FormGroup>
              ))}
            </FormGroup>
          )}

          <FormGroup className="mb-0">
            <Label for="bts-comment" className="small text-uppercase font-weight-bold">
              Additional info (optional)
            </Label>
            <Input
              type="textarea"
              id="bts-comment"
              rows="3"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Any complementary context for the Sales team…"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="warning"
            onClick={handleConfirm}
            disabled={
              !selectedReason ||
              isSubmitting ||
              (selectedReason === currentReasonValue && comment.trim() === currentComment.trim())
            }
          >
            {isSubmitting ? <Spinner size="sm" /> : isAlreadyBackToSales ? "Update" : "Confirm — Back to Sales"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
